import { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';

// MaskedCost — protects a sensitive monetary value from accidental on-screen
// exposure (e.g. during a client demo). Renders a fixed-length mask by default;
// the real value is shown only after a deliberate click and AUTO RE-MASKS after
// `revealMs`. Reveal state is local and never persisted, so every fresh render
// starts masked. The mask length is fixed so it never hints at the magnitude.
//
// Usage: <MaskedCost value="₹17,000" valueClassName={classes.costVal} />

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M1.5 12S5 5 12 5s10.5 7 10.5 7-3.5 7-10.5 7S1.5 12 1.5 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 3l18 18" />
    <path d="M10.6 6.2A9.7 9.7 0 0112 6c7 0 10.5 6 10.5 6a17.9 17.9 0 01-3.4 4.1M6.5 6.6A17.6 17.6 0 001.5 12S5 18 12 18a9.6 9.6 0 004-.9" />
    <path d="M9.9 9.9a3 3 0 004.2 4.2" />
  </svg>
);

function MaskedCost({ value, valueClassName, revealMs = 5000, dark = false }) {
  const [revealed, setRevealed] = useState(false);
  const timerRef = useRef(null);

  // Auto re-mask after the reveal window; always clear on unmount.
  useEffect(() => {
    if (revealed) {
      timerRef.current = setTimeout(() => setRevealed(false), revealMs);
    }
    return () => clearTimeout(timerRef.current);
  }, [revealed, revealMs]);

  const classes = useStyles({ dark });

  const toggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setRevealed((r) => !r);
  };

  return (
    <button
      type="button"
      onClick={toggle}
      className={classes.btn}
      aria-label={revealed ? 'Hide cost' : 'Reveal cost'}
      title={revealed ? 'Hide' : 'Click to reveal'}
    >
      <span className={valueClassName}>{revealed ? value : '•••••'}</span>
      <span className={classes.icon}>{revealed ? <EyeOffIcon /> : <EyeIcon />}</span>
    </button>
  );
}

const useStyles = makeStyles(() => ({
  btn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: 'none',
    background: 'none',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
    lineHeight: 'inherit',
  },
  icon: {
    display: 'flex',
    color: ({ dark }) => (dark ? 'rgba(255,255,255,0.6)' : '#A2A7BC'),
    transition: 'color 150ms ease',
    '$btn:hover &': { color: ({ dark }) => (dark ? '#FEBD1C' : '#111936') },
  },
}));

export default MaskedCost;
